import * as React from 'react'

import { observer } from 'mobx-react'

import IntervalEditor, {
  IIntervalEditorResult,
  NextDayLabel,
} from '~/client/src/shared/components/IntervalEditor'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ProjectDateStore, {
  MINUTES_IN_HOUR,
} from '~/client/src/shared/stores/ui/ProjectDate.store'
import IMsDateInterval from '~/client/src/shared/types/IMsDateInterval'
import {
  formatOperatingIntervalFmt,
  isOperatingIntervalCurrentFmt,
} from '~/client/src/shared/utils/intervalFormatters'
import {
  isWeekdayScopedInterval,
  resolveOperatingIntervalIsDaily,
} from '~/client/src/shared/utils/operatingIntervalMode'
import {
  findDailyOperatingInterval,
  upsertOperatingInterval,
  withSingleDailyInterval,
} from '~/client/src/shared/utils/operatingIntervals'

import {
  buildInheritedHoursRows,
  buildOperatingIntervalSeed,
  buildWeekdayScheduleRows,
  IScheduleRow,
} from '../intervalDefaults'
import EditableTagsCell from './EditableTagsCell'
import { InheritedHoursChip } from './InheritedProjectChip'

/** The lines of a schedule chip, stacked so one chip can hold the whole week. */
function ScheduleLines({ rows }: { rows: IScheduleRow[] }) {
  return (
    <span className="tw:flex tw:flex-col tw:text-left">
      {rows.map(({ label, endsNextDay }) => (
        <span key={label}>
          {label}
          {/* The same marker the editor's field uses, so a window running past
              midnight reads the same wherever it is shown. */}
          {endsNextDay && <NextDayLabel />}
        </span>
      ))}
    </span>
  )
}

interface IProps {
  intervals: IMsDateInterval[]
  projectDateStore: ProjectDateStore
  isBookable: boolean
  onChange: (intervals: IMsDateInterval[]) => void
  /** How the editor surfaces (popover vs full-panel sheet). */
  presentation?: 'popover' | 'sheet'
  /**
   * Navigates to the project settings where the working hours are edited; drives
   * the link in the inherited-default chip's popover. Omitted where navigation
   * isn't available (e.g. mobile).
   */
  onEditProjectHours?: () => void
  /** Mobile surfaces point the inherited-default chip to the desktop version. */
  isMobile?: boolean
}

function HoursOfOperationCell({
  intervals,
  projectDateStore,
  isBookable,
  onChange,
  presentation,
  onEditProjectHours,
  isMobile,
}: IProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  // null = adding a new interval; otherwise the index being edited.
  const [editingIndex, setEditingIndex] = React.useState<number | null>(null)

  // A resource shows a single daily window, so records stored before that rule collapse
  // on read; the next save persists the collapse. Every handler below works off this
  // list, so the indexes it hands the editor match what the user sees.
  const effectiveIntervals = withSingleDailyInterval(intervals)
  const dailyInterval = findDailyOperatingInterval(effectiveIntervals)

  const openAdd = () => {
    setEditingIndex(null)
    setIsOpen(true)
  }

  const closeEditor = () => {
    setEditingIndex(null)
    setIsOpen(false)
  }

  const inheritedHoursRows = buildInheritedHoursRows(projectDateStore)

  // The resource's own week, as one line.
  const weeklyScheduleRows = buildWeekdayScheduleRows(
    projectDateStore,
    effectiveIntervals
      .filter(isWeekdayScopedInterval)
      .map(({ dayOfWeek, startDate, endDate }) => ({
        dayOfWeek,
        startDate,
        endDate: endDate ?? startDate,
      })),
  )

  // The week the editor opens on. Empty while the resource is on a single
  // every-day window, which opens the editor on Everyday instead.
  const weekdayWindows = effectiveIntervals
    .filter(isWeekdayScopedInterval)
    .map(interval => {
      const [startHours, startMinutes] = projectDateStore.getHoursMinutes(
        interval.startDate,
      )
      const [endHours, endMinutes] = projectDateStore.getHoursMinutes(
        interval.endDate ?? interval.startDate,
      )

      return {
        dayOfWeek: interval.dayOfWeek,
        isWorking: true,
        startMinutes: startHours * MINUTES_IN_HOUR + startMinutes,
        endMinutes: endHours * MINUTES_IN_HOUR + endMinutes,
      }
    })

  // The week replaces every recurring window the resource had: weekday windows
  // and an every-day one cannot coexist, which the server enforces too. Dated
  // windows are untouched — they override the schedule on their own days rather
  // than competing with it.
  const handleSaveWeekdays = (results: IIntervalEditorResult[]) => {
    const dated = effectiveIntervals.filter(
      interval => !resolveOperatingIntervalIsDaily(interval),
    )

    onChange([
      ...results.map(({ startDate, endDate, isDaily, dayOfWeek }) => ({
        startDate,
        endDate,
        isDaily,
        dayOfWeek,
      })),
      ...dated,
    ])
    closeEditor()
  }

  const handleSave = (result: IIntervalEditorResult) => {
    const interval: IMsDateInterval = {
      startDate: result.startDate,
      endDate: result.endDate,
      isDaily: result.isDaily,
    }
    onChange(
      upsertOperatingInterval(effectiveIntervals, interval, editingIndex),
    )
    closeEditor()
  }

  return (
    <EditableTagsCell
      isBookable={isBookable}
      isOpen={isOpen}
      onOpen={openAdd}
      onClose={closeEditor}
      cellAffordance="plus"
      triggerLabel={Localization.translator.hoursOfOperation}
      presentation={presentation}
      sheetHeightMode="content"
      popoverClassName="tw:max-h-[min(560px,calc(100vh-96px))] tw:w-[400px] tw:overflow-y-auto tw:px-0"
      // Beside the cell rather than under it: switching Everyday -> Custom grows
      // the editor by seven rows, and a row low in the table has far more room to
      // its side than below it. Its top starts level with the cell and it grows
      // downward from there.
      popoverSide="inline-end"
      popoverAlign="start"
      // Shift, never flip: sliding to stay on screen keeps the popup where the
      // eye left it, whereas flipping vaults it across the trigger mid-edit.
      popoverCollisionAvoidance={{ side: 'shift', align: 'shift' }}
      // The project hours are the resource's operating window only while it has no
      // daily window of its own, so the inherited chip goes away once one exists —
      // otherwise it would state a window validation no longer allows. Removing the
      // daily window brings it (and the fallback) back.
      leadingDisplay={
        dailyInterval || !inheritedHoursRows.length ? undefined : (
          // The project's whole week on one chip, the shape the resource's own
          // schedule takes as well.
          <InheritedHoursChip
            label={<ScheduleLines rows={inheritedHoursRows} />}
            onEditProjectHours={onEditProjectHours}
            isMobile={isMobile}
          />
        )
      }
      // Date windows whose last day has passed are hidden — they can no longer apply
      // to a booking. Hidden, not dropped: the indexes below stay bound to the full
      // list, so editing or dismissing a visible chip leaves them untouched rather
      // than letting an unrelated edit delete them.
      tags={[
        // A weekly schedule is one thing, not seven: its days are grouped into
        // runs, and the whole week is what a chip opens and what dismissing it
        // clears — there is no single day to edit or drop on its own.
        ...(weeklyScheduleRows.length
          ? [
              {
                key: 'weekly-schedule',
                label: <ScheduleLines rows={weeklyScheduleRows} />,
                onClick: () => {
                  setEditingIndex(null)
                  setIsOpen(true)
                },
                onDismiss: () => {
                  setEditingIndex(null)
                  onChange(
                    effectiveIntervals.filter(
                      interval => !isWeekdayScopedInterval(interval),
                    ),
                  )
                },
              },
            ]
          : []),
        ...effectiveIntervals
          .map((interval, index) => ({ interval, index }))
          // The weekly schedule is shown above; what remains is the every-day
          // window and the dated overrides, each its own chip as before.
          .filter(
            ({ interval }) =>
              !isWeekdayScopedInterval(interval) &&
              isOperatingIntervalCurrentFmt(projectDateStore, interval),
          )
          .map(({ interval, index }) => ({
            key: String(index),
            label: formatOperatingIntervalFmt(projectDateStore, interval),
            onClick: () => {
              setEditingIndex(index)
              setIsOpen(true)
            },
            onDismiss: () => {
              setEditingIndex(null)
              onChange(effectiveIntervals.filter((_, i) => i !== index))
            },
          })),
      ]}
      popoverContent={
        <IntervalEditor
          key={editingIndex ?? 'new'}
          initial={buildOperatingIntervalSeed(
            effectiveIntervals,
            editingIndex,
            projectDateStore,
          )}
          projectDateStore={projectDateStore}
          onSave={handleSave}
          onCancel={closeEditor}
          // Hours of operation carry a weekday; a closure does not, so its own
          // cell leaves this off.
          isWeekdayScheduleEnabled={true}
          initialWeekdays={weekdayWindows}
          onSaveWeekdays={handleSaveWeekdays}
          layout={presentation}
          title={Localization.translator.selectX(
            Localization.translator.hoursOfOperation,
          )}
        />
      }
    />
  )
}

export default observer(HoursOfOperationCell)
